import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { getDemoFeatureTreatments } from '@/demo/demoHandlers';
import { isDemoModeAllowed } from '@/demo/demoMode';
import enterpriseFeatureFlags from '@/enterprise/features';
import { DEFAULT_SPLIT_KEY } from '@/lib/features';
import useUserDetails from '@/shared/data/useUserDetails';

export const ENF_VERSION = {
  V0_0: '0.0',
  V0_1: '0.1',
  OFF: 'off',
} as const;

export type EnfVersion = (typeof ENF_VERSION)[keyof typeof ENF_VERSION];

function useAccountSplitKey() {
  const { data: accountId } = useUserDetails(({ me }) => me?.account?.id);
  return accountId ?? DEFAULT_SPLIT_KEY;
}

/**
 * Returns the ENF version treatment as a string: '0.0', '0.1', or 'off'.
 */
export function useEnfVersion(): EnfVersion {
  const splitKey = useAccountSplitKey();
  const featureName = 'enterprise-ui_enableOffNetworkFinance';

  const { treatments } = useSplitTreatments({
    names: [featureName],
    attributes: { trafficType: 'account' },
    splitKey,
  });

  const feature = treatments[featureName];
  let treatment = feature.treatment;

  // If Split fails to load (returns 'control'), fall back to env default
  if (treatment === 'control' && enterpriseFeatureFlags[featureName]) {
    treatment = enterpriseFeatureFlags[featureName];
  }

  // Demo mode overrides
  if (isDemoModeAllowed()) {
    const demoTreatment = getDemoFeatureTreatments()[featureName];
    if (typeof demoTreatment === 'string') {
      treatment = demoTreatment;
    }
  }

  // Validate known treatments
  if (treatment === '0.0') {
    return ENF_VERSION.V0_0;
  }

  if (treatment === '0.1') {
    return ENF_VERSION.V0_1;
  }

  if (treatment === 'off') {
    return ENF_VERSION.OFF;
  }

  // Unknown treatment defaults to 'off'
  return ENF_VERSION.OFF;
}
